import { cva, type VariantProps } from 'class-variance-authority'
import type { ButtonHTMLAttributes } from 'react'
import { cn } from '../../utils/cn.utils'

export const ButtonVariants = cva(
  // [1] 기본 스타일 (Flex 정렬 포함)
  'inline-flex items-center justify-center font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 whitespace-nowrap',
  {
    variants: {
      variant: {
        white: 'border border-border-main bg-white text-black',
        primary: 'bg-[#1976D2] text-white hover:bg-[#1976D2]/80',
        danger: 'bg-red-500 text-white hover:bg-red-600',
        ghost: 'hover:bg-bg-subtle',
        outline: 'border-1 border-black text-black',
        "outline-red": 'border-1 border-red-500 text-red-500',
        gray: "bg-black/10",
        disabled: "bg-black/10 text-black/50 cursor-not-allowed",
        green: "bg-[#2E7D32] text-white",
        black: "bg-black text-white"
      },
      size: {
        sm: 'h-8 text-sm',
        md: 'h-10 text-base',
        lg: 'h-12 text-lg',
      },
      isIcon: {
        true: 'rounded-full', // 아이콘 전용
        false: 'rounded-md',   // 텍스트, 텍스트+아이콘 겸용
      },
    },
    // [2] 조합 변형 (수정)
    compoundVariants: [
      // isIcon: false (텍스트 또는 텍스트+아이콘) -> 패딩과 'gap' 추가
      { isIcon: false, size: 'sm', className: 'px-3 gap-x-1.5' },
      { isIcon: false, size: 'md', className: 'px-4 gap-x-2' },
      { isIcon: false, size: 'lg', className: 'px-5 gap-x-2' },

      // isIcon: true (아이콘 전용) -> 정사각형 너비 추가
      { isIcon: true, size: 'sm', className: 'w-8' },
      { isIcon: true, size: 'md', className: 'w-10' },
      { isIcon: true, size: 'lg', className: 'w-12' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      isIcon: false, // 기본값
    },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof ButtonVariants> {
  children?: React.ReactNode;
  label?: string;
}

export default function Button({
  className,
  variant,
  size,
  isIcon,
  label,
  children,
  ...props
}: ButtonProps) {
  // [7] 접근성 경고: 아이콘 버튼은 스크린 리더를 위해 aria-label이 필수
  if (isIcon && !props['aria-label']) {
    console.warn(
      `[Accessibility Warning] Icon button (isIcon=true) should have an 'aria-label' prop.`,
    )
  }

  return (
    <button
      className={cn(ButtonVariants({ variant, size, isIcon }), className)}
      {...props}
    >
      {children && children}
      {label ? label : "확인"}
    </button>
  )
}